import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { createEmployeeAction } from "../../../../actions/employeeActions";
import {
  backgroundLight,
  buttonRed,
  buttonRedText,
  primaryAlt,
} from "../../../../utils/colors";
import {
  Button,
  Flex,
  HStack,
  Box,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  Tooltip,
  Text,
  Icon,
  IconButton,
  Textarea,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  primary,
  white,
  newBorder,
  textSecondary,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import { FaArrowLeftLong, FaCircleInfo, FaXmark } from "react-icons/fa6";
import FormControlDefault from "../../../../components/input/FormControlDefault";
import FormControlDatePicker from "../../../../components/input/FormControlDatePicker";
import FormControlPhone from "../../../../components/input/FormControlPhone";
import FormControlCountry from "../../../../components/input/FormControlCountry";
import FormControlProfession from "../../../../components/input/FormControlProfession";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIsMobile from "../../../../hooks/useIsMobile";
import { motion } from "framer-motion";
import StatusButtonNew from "../../../../components/buttons/StatusButtonNew";
import FormControlGender from "../../../../components/input/FormControlGender";
import FormControlAddressFull from "../../../../components/input/FormControlAddressFull";
import usePasswordGenerator from "../../../../hooks/usePasswordGenerator";
import { useParams } from "react-router-dom";
import FormControlSelectEnum from "../../../../components/input/FormControlSelectEnum";

const CreateEmployee = ({ defaultRole, close }) => {
  //GENERAL
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  //COLORS
  const { whiteMode, newBorderMode, textPrimaryMode, backgroundLightMode } =
    useThemeColors();

  // STATE
  const [loading, setLoading] = useState(false);
  const [loadingDisplay, setLoadingDisplay] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneAlt, setPhoneAlt] = useState("");
  const [gender, setGender] = useState("");
  const [personalCode, setPersonalCode] = useState("");
  const [status, setStatus] = useState("active");
  const [profession, setProfession] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [iban, setIban] = useState("");
  const [bankName, setBankName] = useState("");
  const [emailSend, setEmailSend] = useState(false);
  const [password, setPassword] = useState("");
  const [isAutoGenerated, setIsAutoGenerated] = useState(true);
  const [contractType, setContractType] = useState("");
  const [compensationBasis, setCompensationBasis] = useState("");
  const [employmentLevel, setEmploymentLevel] = useState("");
  const [employmentLevelDetails, setEmploymentLevelDetails] = useState("");
  const [commissionEnabled, setCommissionEnabled] = useState(false);
  const [commissionDetails, setCommissionDetails] = useState("");

  const [rolePermission, setRolePermission] = useState("employee_default");
  const [address, setAddress] = useState({
    street: "",
    city: "",
    country: "",
    zip: "",
    state: "other",
    display: "",
  });

  // const [employmentStartDate, setEmploymentStartDate] = useState("");

  // REDUX
  // const userInfo = useSelector((state) => state.authLogin.userInfo); // Assuming your state structure
  // const tenantID = userInfo?.tenantID; // Get tenantID from userInfo

  const [manualAddressVisible, setManualAddressVisible] = useState(false);

  const contractTypeOptions = useMemo(
    () => [
      { value: "permanent", label: t("employee.contractTypePermanent") },
      { value: "fixed_term", label: t("employee.contractTypeFixedTerm") },
      { value: "temporary", label: t("employee.contractTypeTemporary") },
    ],
    [t]
  );

  const compensationBasisOptions = useMemo(
    () => [
      { value: "fixed_salary", label: t("employee.compensationFixedSalary") },
      { value: "hourly", label: t("employee.compensationHourly") },
    ],
    [t]
  );

  const employmentLevelOptions = useMemo(
    () => [
      { value: "full_time", label: t("employee.employmentLevelFullTime") },
      { value: "part_time", label: t("employee.employmentLevelPartTime") },
    ],
    [t]
  );

  // ERRORS
  const isFirstNameError = firstName === "";
  const isLastNameError = lastName === "";
  const isBirthDateError = birthDate === "";
  const isPhoneError = phone.length < 4;
  const isPhoneShortError = phone.length < 6;
  const isGenderError = gender === "";
  const isProfessionError = profession === "";

  const isAddressError =
    address.street === "" ||
    address.zip === "" ||
    address.city === "" ||
    address.country === "";

  // Password validation: At least 6 characters and 1 number
  const passwordRegex = /^(?=.*\d).{6,}$/;
  const isPasswordEmpty = password === "";
  const isPasswordInvalid = !passwordRegex.test(password) && !isPasswordEmpty;

  // Email validation: Standard email format
  const isEmailEmpty = email === "";
  const isEmailInvalid =
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) && !isEmailEmpty;

  // TOOLTIP VALIDATION MESSAGES
  const tooltipMessages = [];

  if (isFirstNameError) tooltipMessages.push(t("formError.firstNameRequired"));
  if (isLastNameError) tooltipMessages.push(t("formError.lastNameRequired"));
  // if (isBirthDateError) tooltipMessages.push(t("formError.birthDateRequired"));
  // if (isGenderError) tooltipMessages.push(t("formError.genderRequired"));
  // if (isPhoneShortError) tooltipMessages.push(t("formError.phoneRequired"));
  // if (isAddressError) tooltipMessages.push(t("formError.addressRequired"));
  // if (isProfessionError)
  //   tooltipMessages.push(t("formError.professionRequired"));

  // Email error messages
  // if (isEmailEmpty) {
  //   tooltipMessages.push(t("formError.emailRequired"));
  // } else if (isEmailInvalid) {
  //   tooltipMessages.push(t("formError.emailInvalid"));
  // }

  // // Password error messages
  // if (isPasswordEmpty) {
  //   tooltipMessages.push(t("formError.passwordRequired"));
  // } else if (isPasswordInvalid) {
  //   tooltipMessages.push(t("formError.passwordInvalid"));
  // }

  // CUSTOM REF FOR ACCORDION
  // const professionAccordionRef = useRef(null);
  const bankAccordionRef = useRef(null);
  // const additionalAccordionRef = useRef(null);

  // PASSWORD
  const { generatePassword } = usePasswordGenerator();

  // Generate password on mount
  useEffect(() => {
    const newPassword = generatePassword();
    setPassword(newPassword);
  }, [generatePassword]);

  // Handle password input change
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setIsAutoGenerated(false);
  };

  // Handle password regeneration
  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    setIsAutoGenerated(true);
  };

  // HANDLERS

  const handleAccordionOpen = (ref) => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300); // Small delay for expansion animation
  };

  const toggleManualAddressVisible = () => {
    setManualAddressVisible(!manualAddressVisible);
  };

  useEffect(() => {
    if (employmentLevel !== "part_time" && employmentLevelDetails) {
      setEmploymentLevelDetails("");
    }
  }, [employmentLevel, employmentLevelDetails]);

  useEffect(() => {
    if (!commissionEnabled && commissionDetails) {
      setCommissionDetails("");
    }
  }, [commissionEnabled, commissionDetails]);

  // Handler for profession change
  const onProfessionChange = (selectedOption) => {
    setProfession(selectedOption.value);
    // Assuming selectedOption has the defaultRolePermission property
    const defaultRolePermission = selectedOption.defaultRolePermission;
    if (defaultRolePermission) {
      setRolePermission(defaultRolePermission);
    }
  };

  const handleAddressSelect = (selectedAddress) => {
    setAddress({
      street: selectedAddress.street,
      city: selectedAddress.city,
      country: selectedAddress.country,
      zip: selectedAddress.zip,
      state: selectedAddress.state,
      display: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.zip}`,
    });
  };

  // HANDLERS
  const handleGenderChange = (selectedOption) => {
    setGender(selectedOption.value);
  };

  const closeHandler = () => {
    close();
  };

  // SUBMIT

  const submitHandler = (e) => {
    e.preventDefault();

    let genderValue = null;

    if (gender) {
      genderValue = gender;
    }

    const employee = {
      firstName,
      lastName,
      birthDate,
      personalCode,
      gender: genderValue,
      profession,
      licenseNumber,

      email,

      phone,
      phoneAlt,

      address: {
        street: address.street,
        zip: address.zip,
        city: address.city,
        country: address.country,
        state: address.state,
      },
      iban,
      bankName,
      status,
      contractType: contractType || null,
      compensationBasis: compensationBasis || null,
      employmentLevel: employmentLevel || null,
      employmentLevelDetails:
        employmentLevel === "part_time" ? employmentLevelDetails : "",
      commissionEnabled,
      commissionDetails: commissionEnabled ? commissionDetails : "",
    };

    // console.log("employee", employee);

    dispatch(createEmployeeAction(slug, employee));

    // console.log("submitted data", {
    //   firstName,
    //   lastName,
    //   birthDate,
    //   personalCode,
    //   gender,

    //   profession,
    //   licenseNumber,
    //   rolePermission,

    //   email,
    //   password,
    //   emailSend,

    //   phone,
    //   phoneAlt,
    //   address,

    //   iban,
    //   bankName,

    //   status,
    // });
  };

  // USE EFFECT
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 300); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  // // UseEffect to update gender based on personalCode
  // useEffect(() => {
  //   const firstChar = personalCode.charAt(0);
  //   if (firstChar === "3" || firstChar === "5") {
  //     setGender("male");
  //   } else if (firstChar === "4" || firstChar === "6") {
  //     setGender("female");
  //   }
  // }, [personalCode]); // Dependency array includes personalCode

  // DATES
  const minBirthDate = new Date(1900, 0, 1); // Set minimum date to Jan 1, 1900
  const maxBirthDate = new Date(); // Set maximum date to today
  const minEmploymentStartDate = new Date(2000, 0, 1); // Set minimum date to Jan 1, 1900
  const maxEmploymentStartDate = new Date(); // Set maximum date to today

  // LOGS

  // console.log("firstName", firstName);
  // console.log("lastName", lastName);
  // console.log("birthDate", birthDate);
  // console.log("personalCode", personalCode);
  // console.log("gender", gender);

  // console.log("profession", profession);
  // console.log("licenseNumber", licenseNumber);
  // console.log("rolePermission", rolePermission);

  // console.log("email", email);
  // console.log("password", password);
  // console.log("emailSend", emailSend);

  // console.log("phone", phone);
  // console.log("phoneAlt", phoneAlt);
  // console.log("address street", address.street);
  // console.log("address zip", address.zip);
  // console.log("address city", address.city);
  // console.log("address country", address.country);
  // console.log("address state", address.state);

  // console.log("iban", iban);
  // console.log("bankName", bankName);

  // console.log("status", status);

  return (
    <>
      {loading || loadingDisplay ? (
        <Flex bg={whiteMode} align="center" justify="center" w="100%" h="100%">
          <Spinner color={primary} size="lg" />
        </Flex>
      ) : (
        <Box
          position="relative"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          w="100%"
          h="100%"
        >
          <form
            onSubmit={submitHandler}
            style={{
              height: "100%",
            }}
          >
            <Flex w="100%" h="100%" direction="column">
              {/* HEADER START */}
              <Flex
                justify="space-between"
                align="center"
                p={["1.25rem 1.5rem 1.25rem", "1.25rem 2rem 1.25rem"]}
                borderBottom={`1px solid ${newBorderMode}`}
                mb="0rem"
                position="relative"
                zIndex={90}
                w="100%"
              >
                <Flex align="center">
                  {!isMobile && (
                    <IconButton
                      bg={backgroundLightMode}
                      border={`1px solid ${backgroundLightMode}`}
                      color={textSecondary}
                      icon={<FaArrowLeftLong />}
                      borderRadius="10.75rem"
                      fontSize="0.875rem"
                      onClick={closeHandler}
                      mr="1rem"
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: primaryAlt,
                      }}
                    />
                  )}

                  <Flex align="center">
                    <Heading
                      fontSize={["1rem", "1.125rem"]}
                      color={textPrimaryMode}
                      fontWeight="600"
                      mr={4}
                    >
                      {t("employee.createEmployee")}
                    </Heading>
                    {/* {status} */}
                  </Flex>
                </Flex>

                <Flex align="center" justify="flex-end" gap="1rem">
                  {!isMobile && (
                    <Tooltip
                      p="0.5rem 1rem"
                      borderRadius="1rem"
                      label={
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="500"
                            textAlign="center"
                          >
                            {t("employee.newEmployeeExplanation")}
                          </Text>
                        </Flex>
                      }
                    >
                      <Flex align="center">
                        <Icon
                          as={FaCircleInfo}
                          fontSize="0.875rem"
                          color={primaryAlt}
                        />
                      </Flex>
                    </Tooltip>
                  )}

                  <StatusButtonNew
                    isSmall
                    currentStatus={status}
                    options="createEmployee"
                    onUpdateStatus={setStatus}
                    size={["sm", "md"]}
                    p={["0 1rem", "0 2rem"]}
                    fontSize={["0.75rem", "0.875rem"]}
                  />

                  {isMobile && (
                    <IconButton
                      position="relative"
                      // top="1.5rem"
                      // right="1.5rem"
                      size="sm"
                      bg={backgroundLight}
                      color={textSecondary}
                      onClick={closeHandler}
                      borderRadius="10rem"
                      _hover={{ bg: primaryAlt, color: white }}
                    >
                      <Icon as={FaXmark} />
                    </IconButton>
                  )}
                </Flex>
              </Flex>
              {/* HEADER END */}

              {/* MAIN START */}
              <Flex
                w="100%"
                h="100%"
                direction="column"
                overflowY="auto"
                p={["1rem 0rem 7rem", "1rem 2rem 7rem"]}
              >
                {/* EMPLOYEE DETAILS START */}
                <Flex direction="column" w="100%">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {t("employee.details")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem 0.25rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDefault
                        autoFocus
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.firstName")}
                        value={firstName}
                        placeholder={t("placeholder.firstName")}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                        }}
                        isError={isFirstNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.firstNameRequired")}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.lastName")}
                        value={lastName}
                        placeholder={t("placeholder.lastName")}
                        onChange={(e) => {
                          setLastName(e.target.value);
                        }}
                        isError={isLastNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.lastNameRequired")}
                      />
                    </Flex>

                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDatePicker
                        type="date"
                        size="lg"
                        labelFontSize="0.8rem"
                        // isRequired
                        label={t("form.birthDate")}
                        minDate={minBirthDate}
                        maxDate={maxBirthDate}
                        // value={birthDate}
                        // value="1970-01-01T00:00:34.357Z"
                        placeholder={t("placeholder.birthDate")}
                        onChange={(newValue) => {
                          setBirthDate(newValue);
                        }}
                        onOpen={() => {}}
                        isError={isBirthDateError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.birthDateRequired")}
                        zIndex={90006}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        // isRequired
                        label={t("form.personalCode")}
                        value={personalCode}
                        placeholder={t("placeholder.personalCode")}
                        onChange={(e) => {
                          setPersonalCode(e.target.value);
                        }}
                        // isError={isPersonalCodeError}
                        w={["100%", "50%", "50%"]}
                        // errorMessage={t("formError.personalCodeRequired")}
                      />
                    </Flex>

                    <Flex
                      w="100%"
                      direction={["column", "row", "row"]}
                      pr="2rem"
                    >
                      <FormControlGender
                        value={gender}
                        // isRequired
                        label={t("patient.gender")}
                        labelFontSize="0.8rem"
                        placeholder={t("placeholder.gender")}
                        onChange={handleGenderChange}
                        w={["100%", "100%", "50%"]}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                {/* EMPLOYEE DETAILS END */}

                {/* PROFESSION DETAILS START */}
                <Flex direction="column" w="100%" m="1.5rem 0 0">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {t("employee.professionDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem 0.25rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlProfession
                        slug={slug}
                        label={t("form.profession")}
                        labelFontSize="0.8rem"
                        isSearchable={true}
                        value={profession}
                        placeholder={t("placeholder.profession")}
                        onChange={onProfessionChange}
                        isRequired={false}
                        // isError={isProfessionError}
                        errorMessage={t("formError.professionRequired")}
                        w={["100%", "50%"]}
                        onProfessionChange={onProfessionChange}
                        zIndex={90005}
                      />

                      <FormControlDefault
                        type="text"
                        labelFontSize="0.8rem"
                        size="lg"
                        // isRequired
                        label={t("form.licenseNumber")}
                        value={licenseNumber}
                        placeholder={t("placeholder.licenseNumber")}
                        onChange={(e) => {
                          setLicenseNumber(e.target.value);
                        }}
                        w={["100%", "50%"]}
                      />
                    </Flex>
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    ></Flex>
                  </Flex>
                </Flex>
                {/* EMPLOYEE PROFESSION DETAILS END */}

                {/* EMPLOYEE CONTRACT DETAILS START */}
                <Flex direction="column" w="100%" m="1.5rem 0 0">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {t("employee.contractDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p={["1.25rem 1.5rem 0.25rem", "1.25rem 1.5rem 0.25rem"]}
                    gap="1.25rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlSelectEnum
                        label={t("employee.contractType")}
                        value={contractType}
                        onChange={setContractType}
                        options={contractTypeOptions}
                        placeholder={t("employee.contractSelectPlaceholder")}
                        w={["100%", "50%", "50%"]}
                      />
                      <FormControlSelectEnum
                        label={t("employee.compensationBasis")}
                        value={compensationBasis}
                        onChange={setCompensationBasis}
                        options={compensationBasisOptions}
                        placeholder={t("employee.contractSelectPlaceholder")}
                        w={["100%", "50%", "50%"]}
                      />
                    </Flex>

                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlSelectEnum
                        label={t("employee.employmentLevel")}
                        value={employmentLevel}
                        onChange={setEmploymentLevel}
                        options={employmentLevelOptions}
                        placeholder={t("employee.contractSelectPlaceholder")}
                        w={["100%", employmentLevel === "part_time" ? "50%" : "100%", employmentLevel === "part_time" ? "50%" : "100%"]}
                      />
                      {employmentLevel === "part_time" && (
                        <FormControlDefault
                          type="text"
                          size="lg"
                          labelFontSize="0.8rem"
                          label={t("employee.employmentLevelDetails")}
                          value={employmentLevelDetails}
                          placeholder={t("employee.employmentLevelDetails")}
                          onChange={(e) => setEmploymentLevelDetails(e.target.value)}
                          w={["100%", "50%", "50%"]}
                        />
                      )}
                    </Flex>

                    <Flex
                      gap={["1rem", "2rem"]}
                      direction={["column", "row"]}
                      align={["flex-start", "center"]}
                    >
                      <FormControl display="flex" alignItems="center" w={["100%", "50%", "33%"]}>
                        <FormLabel htmlFor="create-commission-enabled" m="0" fontSize="sm" color={textPrimaryMode}>
                          {t("employee.commissionEnabled")}
                        </FormLabel>
                        <Switch
                          id="create-commission-enabled"
                          isChecked={commissionEnabled}
                          onChange={(event) => setCommissionEnabled(event.target.checked)}
                          colorScheme="teal"
                        />
                      </FormControl>
                      {commissionEnabled && (
                        <FormControl w={["100%", "50%", "67%"]}>
                          <FormLabel fontSize="sm" color={textPrimaryMode}>
                            {t("employee.commissionDetails")}
                          </FormLabel>
                          <Textarea
                            value={commissionDetails}
                            onChange={(event) => setCommissionDetails(event.target.value)}
                            placeholder={t("employee.commissionDetails")}
                            border={`1px solid ${newBorderMode}`}
                            borderRadius="0.75rem"
                            fontSize="0.938rem"
                            fontWeight="500"
                            minH="120px"
                          />
                        </FormControl>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
                {/* EMPLOYEE CONTRACT DETAILS END */}

                {/* EMPLOYEE LOGIN DETAILS START */}
                {/* <Flex direction="column" w="100%" mt="1.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                  >
                    {isMobile
                      ? t("employee.loginDetails")
                      : t("employee.loginDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius="1rem"
                    p="1.25rem 1.5rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDefault
                        type="email"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.email")}
                        value={email}
                        placeholder={t("placeholder.email")}
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                       
                        w="50%"
                        errorMessage={t("formError.emailRequired")}
                      />

                      <FormControlPassword
                        isRequired={true}
                        label={t("form.password")}
                        labelFontSize="0.8rem"
                        value={password}
                        onChange={handlePasswordChange}
                        w="50%"
                        explainTooltip
                        explainTooltipLabel={
                          <Flex
                            direction="column"
                            justify="center"
                            align="center"
                            fontWeight="400"
                          >
                            <Text
                              fontSize="xs"
                              textAlign="left"
                              color={white}
                              fontWeight="500"
                            >
                              <Icon
                                as={FaCircleInfo}
                                fontSize="0.875rem"
                                position="relative"
                                top="3px"
                                mr={1}
                              />{" "}
                              {t("employee.passwordTooltipExplanation")}
                            </Text>
                          </Flex>
                        }
                        rightLabelElement={
                          <Tooltip
                            p="0.5rem 1rem"
                            borderRadius="1rem"
                            placement="top"
                            label={t("form.generatePassword")}
                            fontSize="xs"
                          >
                            <IconButton
                              bg="transparent"
                              aria-label={t("form.generatePassword")}
                              icon={<FaRedo fontSize="0.6rem" />}
                              onClick={handleGeneratePassword}
                              size="xs"
                            />
                          </Tooltip>
                        }
                      />
                    </Flex>
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlRolePermission
                        isRequired
                        isSearchable={false}
                        label={t("form.rolePermission")}
                        labelFontSize="0.8rem"
                        value={rolePermission}
                        placeholder={t("placeholder.rolePermission")}
                        onChange={(selectedOption) => {
                          setRolePermission(selectedOption.value);
                        }}
                        w="50%"
                        explainTooltip
                        explainTooltipLabel={
                          <Flex
                            direction="column"
                            justify="center"
                            align="center"
                            fontWeight="400"
                          >
                            <Text
                              fontSize="xs"
                              textAlign="left"
                              color={white}
                              fontWeight="500"
                            >
                              <Icon
                                as={FaCircleInfo}
                                fontSize="0.875rem"
                                position="relative"
                                top="3px"
                                mr={1}
                              />{" "}
                              {t("employee.rolePermissionExplanation")}
                            </Text>

                            <Flex direction="column" m="1rem 0 0" gap="0.5rem">
                              <Text fontSize="xs" textAlign="left">
                                <strong>
                                  {t("rolePermission.employee_default")}:{" "}
                                </strong>
                                {t(
                                  "employee.employeeRolePermissionExplanation"
                                )}
                              </Text>

                              <Text fontSize="xs" textAlign="left">
                                <strong>
                                  {t("rolePermission.employee_coordinator")}:{" "}
                                </strong>
                                {t(
                                  "employee.coordinatorRolePermissionExplanation"
                                )}
                              </Text>

                              <Text fontSize="xs" textAlign="left">
                                <strong>
                                  {t("rolePermission.employee_doctor")}:{" "}
                                </strong>
                                {t("employee.doctorRolePermissionExplanation")}
                              </Text>

                              <Text fontSize="xs" textAlign="left">
                                <strong>{t("rolePermission.admin")}: </strong>
                                {t("employee.adminRolePermissionExplanation")}
                              </Text>
                            </Flex>
                          </Flex>
                        }
                      />

                      <FormControl w="50%">
                        <FormLabel
                          fontSize="0.8rem"
                          color={textPrimary}
                          htmlFor="employee-email-send"
                        >
                          Siųsti darbuotojui el. laišką su prisijungimo info?

                        </FormLabel>
                        <Switch
                          m="0.5rem 0 0"
                          id="employee-email-send"
                          size="lg"
                          onChange={() => setEmailSend(!emailSend)}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                </Flex> */}
                {/* EMPLOYEE LOGIN DETAILS END */}

                {/* EMPLOYEE ADDRESS START */}
                <Flex direction="column" w="100%" mt="1.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1.5rem", "initial"]}
                  >
                    {isMobile
                      ? t("employee.addressContactDetailsMobile")
                      : t("employee.addressContactDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlPhone
                        label={t("form.phone")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phone}
                        onChange={(newPhone) => {
                          setPhone(newPhone);
                        }}
                        isRequired={false}
                        isError={isPhoneError}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90004}
                      />

                      <FormControlPhone
                        label={t("form.phoneAlt")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phoneAlt}
                        onChange={(newPhone) => {
                          setPhoneAlt(newPhone);
                        }}
                        isRequired={false}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90003}
                      />
                    </Flex>

                    {!manualAddressVisible && (
                      <Flex direction="column" w="100%" p="0rem">
                        <FormControlAddressFull
                          label={t("form.address")}
                          size="lg"
                          labelFontSize="0.8rem"
                          placeholder={t("placeholder.address")}
                          onAddressSelect={handleAddressSelect}
                          isRequired={false}
                          currentValue={address.display}
                          w="100%"
                          googleApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                          // onBlur={handleAddressBlur}
                          zIndex={90002}
                        />
                        <Flex
                          align="center"
                          justify="center"
                          gap="1rem"
                          p="0.5rem 0 0"
                        >
                          <Flex
                            position="relative"
                            m="0rem 0 0"
                            justify="flex-end"
                            w="100%"
                          >
                            <Button
                              bg="transparent"
                              onClick={toggleManualAddressVisible}
                              color={primary}
                              fontSize="xs"
                              size="xs"
                              fontWeight="500"
                              p="0.25rem 0.75rem"
                              borderRadius="10rem"
                              opacity="1"
                              transition="all 0.3s"
                              _hover={{
                                bg: white,
                                opacity: "1",
                              }}
                            >
                              {t("onboarding.enterAddressManual")}
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}

                    {manualAddressVisible && (
                      <>
                        <Flex
                          direction="column"
                          align="center"
                          justify="center"
                          p="0rem"
                        >
                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              size="lg"
                              labelFontSize="0.8rem"
                              isRequired={false}
                              label={t("form.street")}
                              value={address.street}
                              placeholder={t("placeholder.street")}
                              onChange={(e) =>
                                setAddress({
                                  ...address,
                                  street: e.target.value,
                                })
                              }
                              w={["100%", "50%", "50%"]}
                            />

                            <FormControlDefault
                              type="text"
                              isRequired={false}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.zip")}
                              value={address.zip}
                              placeholder={t("placeholder.zip")}
                              onChange={(e) =>
                                setAddress({ ...address, zip: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                            />
                          </HStack>

                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              isRequired={false}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.city")}
                              value={address.city}
                              placeholder={t("placeholder.city")}
                              onChange={(e) =>
                                setAddress({ ...address, city: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.cityRequired")}
                            />

                            <FormControlCountry
                              label={t("form.country")}
                              size="lg"
                              labelFontSize="0.8rem"
                              value={address.country}
                              isRequired={false}
                              onChange={(selectedOption) => {
                                setAddress(
                                  Object.assign({}, address, {
                                    country: selectedOption.value,
                                  })
                                );
                              }}
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.countryRequired")}
                            />
                          </HStack>
                          <Flex align="center" justify="center" gap="1rem">
                            <Flex
                              position="relative"
                              m="0rem 0 0"
                              justify="center"
                            >
                              <Button
                                bg="transparent)"
                                onClick={toggleManualAddressVisible}
                                color={primary}
                                variant="link"
                                fontSize="xs"
                                fontWeight="500"
                                p="0.25rem 0.75rem"
                                borderRadius="10rem"
                                _hover={{
                                  bg: white,
                                }}
                              >
                                {t("onboarding.enterAddressGoogle")}
                              </Button>
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>
                {/* EMPLOYEE ADDRESS END */}

                {/* EMPLOYEE BANK DETAILS START */}
                <Flex
                  direction="column"
                  w="100%"
                  p={["0rem 0rem", "0rem 0"]}
                  m="1rem 0 0"
                  className="bankDetailsAccordion"
                >
                  <Accordion allowToggle>
                    <AccordionItem border="none">
                      <AccordionButton
                        borderRadius="0rem"
                        borderBottom={`1px solid ${newBorder}`}
                        transition="0.2s all"
                        p={["0.75rem 1.5rem", "0.75rem 0"]}
                        _hover={{
                          bg: "none",
                          pl: 2,
                          transition: "0.2s all",
                        }}
                        _expanded={{
                          borderBottom: "none",
                        }}
                        onClick={() => handleAccordionOpen(bankAccordionRef)}
                      >
                        <Flex align="center" justify="space-between" w="100%">
                          <Text
                            fontSize="0.875rem"
                            fontWeight="600"
                            color={textPrimaryMode}
                            // m="0 0 0.5rem"
                          >
                            {t("employee.bankDetails")}
                          </Text>

                          <AccordionIcon color={textSecondary} />
                        </Flex>
                      </AccordionButton>

                      <AccordionPanel pb={0} px={0} m="-0.5rem 0 0.5rem">
                        <div ref={bankAccordionRef} />
                        <Flex
                          direction="column"
                          w="100%"
                          bg={backgroundLight}
                          borderRadius={["0rem", "1rem"]}
                          p="1.5rem"
                        >
                          <Flex gap={4} w="100%" direction={["column", "row"]}>
                            <FormControlDefault
                              type="text"
                              size="lg"
                              // isRequired
                              label={t("form.iban")}
                              labelFontSize="0.8rem"
                              value={iban}
                              placeholder={t("placeholder.iban")}
                              onChange={(e) => {
                                setIban(e.target.value);
                              }}
                              // isTouched={firstNameTouched}
                              // isError={isFirstNameError}
                              w={["100%", "50%"]}
                              // errorMessage={t("formError.firstNameRequired")}
                            />

                            <FormControlDefault
                              type="text"
                              size="lg"
                              // isRequired
                              label={t("form.bankName")}
                              labelFontSize="0.8rem"
                              // isTouched={firstNameTouched}
                              // isError={isFirstNameError}
                              value={bankName}
                              placeholder={t("placeholder.bankName")}
                              onChange={(e) => {
                                setBankName(e.target.value);
                              }}
                              w={["100%", "50%"]}
                              // isError={isFirstNameError}
                              // errorMessage={t("formError.firstNameRequired")}

                              // isTouched={firstNameTouched}
                              // isError={isFirstNameError}
                            />

                            {/* <FormControlBank
                              isSearchable={true}
                              label={t("form.bankName")}
                              size="lg"
                              labelFontSize="0.8rem"
                              value={bankName}
                              placeholder={t("placeholder.bankName")}
                              onChange={(selectedOption) => {
                                setBankName(selectedOption.value);
                              }}
                              w="50%"
                            /> */}
                          </Flex>
                        </Flex>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
                {/* EMPLOYEE BANK DETAILS END */}
                {/* BOTTOM START */}
                <Flex
                  align="center"
                  justify="flex-end"
                  position={["relative", "fixed"]}
                  bottom={["initial", "2rem"]}
                  right={["initial", "2rem"]}
                  gap="0.5rem"
                  zIndex={90006}
                  p={["1.5rem 1.5rem", "initial"]}
                >
                  <Tooltip
                    bg={buttonRed}
                    color={buttonRedText}
                    padding="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      tooltipMessages.length > 0 ? (
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          {tooltipMessages.map((message, index) => (
                            <Text
                              key={index}
                              color={buttonRedText}
                              fontSize="xs"
                              fontWeight="500"
                              textAlign="center"
                            >
                              {message}
                            </Text>
                          ))}
                        </Flex>
                      ) : null
                    }
                    isDisabled={tooltipMessages.length === 0} // Disable tooltip if no errors
                  >
                    <Button
                      type="submit"
                      minWidth="10rem"
                      bg={textPrimaryMode}
                      size="lg"
                      color={whiteMode}
                      fontSize="sm"
                      borderRadius="10rem"
                      border={`1px solid ${whiteMode}`}
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: `1px solid ${whiteMode}`,
                      }}
                      onClick={submitHandler}
                      isDisabled={tooltipMessages.length > 0} // Disable if any errors exist
                    >
                      {t("employee.createEmployee")}
                    </Button>
                  </Tooltip>
                </Flex>

                {/* BOTTOM END */}
              </Flex>
              {/* MAIN END */}
            </Flex>
          </form>
        </Box>
      )}
    </>
  );
};

export default CreateEmployee;
